import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {
  InlineBanner,
  InlineBannerLayoutTypesEnum,
  InlineBannerTypesEnum,
} from '../../../../../../libs/shared/ui/src/components/banners/inlineBanner/inlineBanner';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Dialog} from '../../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogContent} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {DialogFooter} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogFooter';
import {DialogHeader} from '../../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {MacroInput, MacroInputProps} from './macroInput';

/*
 * Props.
 */

export interface MacroInputDialogProps extends MacroInputProps {
  macroName: string;
  shouldShowMoveActionWarning?: boolean;
  onDismissMoveActionWarning?: () => void;
  isDisabled?: boolean;
  onRequestClose: () => void;
  onSubmit: () => void;
}

/*
 * Styles.
 */

const StyledDialog = styled(Dialog)`
  max-height: 580px;
`;

const StyledDialogContent = styled(DialogContent)`
  padding: 20px;
  overflow-y: auto;
`;

const StyledInfoBanner = styled(InlineBanner)`
  margin-top: 20px;
`;

/*
 * Component.
 */

export const MacroInputDialog: FC<MacroInputDialogProps> = (props) => {
  const {
    macroName,
    isDisabled,
    onRequestClose,
    onSubmit,
    shouldShowMoveActionWarning,
    onDismissMoveActionWarning,
    ...childProps
  } = props;

  return (
    <StyledDialog onRequestClose={onRequestClose}>
      <DialogHeader title={<Trans id="macro-input-dialog-title">{macroName}</Trans>} />
      <StyledDialogContent noMarginBottom noMarginTop noMarginLeftRight>
        <MacroInput
          // eslint-disable-next-line react/jsx-props-no-spreading
          {...childProps}
        />
        {shouldShowMoveActionWarning && (
          <StyledInfoBanner
            type={InlineBannerTypesEnum.INFO}
            layoutType={InlineBannerLayoutTypesEnum.MULTILINE}
            onClose={onDismissMoveActionWarning}
          >
            <Trans id="macro-input-dialog-move-warning">
              You may no longer be able to view this conversation if it’s moved to an inbox you don’t have
              access to.
            </Trans>
          </StyledInfoBanner>
        )}
      </StyledDialogContent>
      <DialogFooter>
        <ButtonGroupSpacer />
        <ButtonGroup>
          <Button onClick={onRequestClose}>
            <Trans id="macro-input-dialog-button-cancel">Cancel</Trans>
          </Button>
          <Button variant={ButtonVariantsEnum.PRIMARY} isDisabled={isDisabled} onClick={onSubmit}>
            <Trans id="macro-input-dialog-button-ok">Submit</Trans>
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </StyledDialog>
  );
};
